import type { GatewayPrincipalUsageSummaryView } from '@/types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildUsageChartRows,
  formatUsageCost,
  formatUsageInteger,
  type UsageChartRow,
} from './usageChartData';

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-6 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

export function GatewayUsageCostChart({
  summary,
}: {
  summary: GatewayPrincipalUsageSummaryView | null;
}) {
  const data = buildUsageChartRows(summary);
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div className="text-sm font-medium text-foreground">最近 30 天每日花费</div>
      <div className="mt-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) {
                  return null;
                }
                const row = payload[0]?.payload as UsageChartRow;
                return (
                  <div className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-md">
                    <div className="mb-2 text-xs font-medium text-slate-900">{label}</div>
                    <div className="space-y-1">
                      <TooltipRow label="花费" value={formatUsageCost(row.costUsd)} />
                      <TooltipRow label="请求数" value={formatUsageInteger(row.requestCount)} />
                    </div>
                  </div>
                );
              }}
            />
            <Line
              type="monotone"
              dataKey="costUsd"
              stroke="#f97316"
              strokeWidth={2.5}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function GatewayUsageTokensChart({
  summary,
}: {
  summary: GatewayPrincipalUsageSummaryView | null;
}) {
  const data = buildUsageChartRows(summary);
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div className="text-sm font-medium text-foreground">最近 30 天每日 Tokens</div>
      <div className="mt-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) {
                  return null;
                }
                const row = payload[0]?.payload as UsageChartRow;
                return (
                  <div className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-md">
                    <div className="mb-2 text-xs font-medium text-slate-900">{label}</div>
                    <div className="space-y-1">
                      <TooltipRow label="Tokens" value={formatUsageInteger(row.totalTokens)} />
                      <TooltipRow label="请求数" value={formatUsageInteger(row.requestCount)} />
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="totalTokens" fill="#2563eb" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
